import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, EnvelopeSimple } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      setUser(r.data.user);
      navigate("/", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Sign-in failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#F7F7F8] p-6" data-testid="login-page">
      <div className="w-full max-w-md bg-white border border-[#E2E2E5] p-10">
        <div className="flex items-center gap-3 mb-8">
          <img src="/brand/stryde-mark.jpg" alt="Stryde" className="w-12 h-12 object-contain" />
          <div>
            <div className="font-display font-black text-xl leading-none">
              <span className="text-[#006CFF]">STRYDE</span>
            </div>
            <div className="text-[10px] font-mono uppercase text-[#5C5E66] tracking-[0.18em] mt-1">Medics</div>
          </div>
        </div>

        <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] mb-2">Authentication</div>
        <h1 className="font-display font-black text-3xl tracking-tight mb-2">Sign in</h1>
        <p className="text-sm text-[#5C5E66] mb-8 leading-relaxed">
          Use your authorized Stryde Research email. Need access? Contact your administrator.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Email</Label>
            <div className="relative mt-1">
              <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5E66]" />
              <Input
                data-testid="login-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@stryderesearch.com"
                className="rounded-none pl-9 h-11"
                required
              />
            </div>
          </div>

          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Password</Label>
            <div className="relative mt-1">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5E66]" />
              <Input
                data-testid="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-none pl-9 h-11 font-mono"
                required
              />
            </div>
          </div>

          {error && (
            <div data-testid="login-error" className="text-xs font-mono px-3 py-2 bg-[#C6282814] text-[#C62828] border border-[#C6282833]">
              {error}
            </div>
          )}

          <Button
            type="submit"
            data-testid="login-submit-btn"
            disabled={submitting}
            className="w-full bg-[#006CFF] hover:bg-[#0052CC] text-white rounded-none gap-2 h-12 text-sm"
          >
            {submitting ? "SIGNING IN..." : "Sign In"}
          </Button>
        </form>

        <div className="mt-6 pt-6 border-t border-[#E2E2E5] text-[11px] font-mono uppercase tracking-wider text-[#5C5E66] flex items-start gap-2">
          <Lock size={12} className="mt-0.5 flex-shrink-0" />
          <span className="normal-case tracking-normal">Whitelisted Stryde Research accounts only. After 5 failed attempts, login locks for 15 minutes.</span>
        </div>
      </div>
    </div>
  );
}
